// src/pages/admin/IntegrationAppList.tsx
import React, { useEffect, useState } from "react"; 
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { IconButton, TextButton } from "../../components/ButtonComponents";
import { Edit, Trash2 ,ArrowRight} from "lucide-react";
import { IntegrationQueryList } from "./IntegrationQueryList"; // <-- Yeni import
import { toast } from "react-toastify"; 
import { useDynamicScrollHeight } from "../../hooks/useDynamicScrollHeight";
import { Filter } from "../../types/filter"; 

import { useTableHeader } from "../../hooks/useTableHeader";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { IntegrationAppDialog } from "./IntegrationAppDialog"; 
import { IntegrationApplication } from "../../types/integration"; 
import { useIntegrationStore } from "../../store/integrationStore";
import { QueryParams } from "../../types";

export const IntegrationAppList = () => {
  // Store'dan gerekli durumları ve yeniden isimlendirilmiş metotları çekme
  const { 
    data: applications, // data, generic store'dan geliyor
    total: appTotal, 
    loading: appLoading, 
    queryParams, // Generic store'dan gelen params
    setQueryParams, // Generic store'dan gelen metot
    
    fetchDataDynamic, // <-- Metot Adı Düzeltildi
    createItem,       // <-- Metot Adı Düzeltildi
    updateItem,       // <-- Metot Adı Düzeltildi
    deleteItem,       // <-- Metot Adı Düzeltildi
  } = useIntegrationStore(); 
  // DATATABLE STATE
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(queryParams.pageSize);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [appToEdit, setAppToEdit] = useState<IntegrationApplication | null>(null);
  const scrollHeight = useDynamicScrollHeight(260);
const [appForQueries, setAppForQueries] = useState<IntegrationApplication | null>(null); 
const handleManageQueries = (app: IntegrationApplication) => {
    console.log("Managing queries for app:", app);
    setAppForQueries(app); // Hangi uygulamanın sorgularını yöneteceğimizi state'e kaydet
  };

  const handleCloseQueryModal = () => {
    setAppForQueries(null); // Modal kapandığında state'i sıfırla
  };
  const handleNew = () => {
    setAppToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = (app: IntegrationApplication) => {
    setAppToEdit(app);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAppToEdit(null);
  };

  const { confirm, modal: confirmModal } = useConfirmModal();

  const handleDeleteClick = async (app: IntegrationApplication) => {
    const ok = await confirm(`${app.name} uygulamasını silmek istiyor musun? (İlişkili tüm sorgular silinecektir)`);

    if (ok) {
      try {
        await deleteItem(app.id); // Metot Adı Güncellendi
        toast.success("Uygulama silindi");
        // Silme sonrası listeyi yenileyecek, generic store içinde mevcut.
      } catch (error) {
        toast.error("Silme işlemi başarısız oldu. İlişkili firmalar veya sorgular olabilir.");
      }
    }
  };
  
  const handleSaveApp = async (appData: any) => {
    try {
      if (appToEdit) {
        await updateItem(appToEdit.id, appData); // Metot Adı Güncellendi
        toast.success(`Uygulama Güncellendi: ${appData.name}`);
      } else {
        await createItem(appData); // Metot Adı Güncellendi
        toast.success(`Yeni Uygulama Eklendi: ${appData.name}`);
      }
      // Kayıt/güncelleme sonrası liste generic store içinde yenileniyor, burada sadece modal kapatılmalı.
      handleCloseModal();

    } catch (error) {
      toast.error("İşlem sırasında hata oluştu");
    }
  };

  // --- DataTable Header ve Filtreleme
  const { header, dynamicFilter } = useTableHeader({
    title: "Entegrasyon Uygulama Tanımları",
    placeholder: "Uygulama Adı/Tipi Ara",
    searchFields: [
      { field: "name", operator: "contains" },
      { field: "externalType", operator: "contains" }, 
    ],
    debounceMs: 350,
    actions: (
      <TextButton onClick={handleNew} className="btn-new">
        Yeni Uygulama Ekle
      </TextButton>
    ),
  });

  // Pagination
  const handlePage = (event: any) => {
    const newParams = { 
        ...queryParams, 
        pageIndex: event.page + 1, 
        pageSize: event.rows 
    };
    setFirst(event.first);
    setRows(event.rows);
    setQueryParams(newParams); // QueryParams'ı state'e kaydet
    fetchDataDynamic(newParams); // Yeni parametrelerle veriyi çek
  };

  // Fetch (pageIndex, pageSize, filter)
  useEffect(() => {
    const pageIndex = Math.floor(first / (rows ?? queryParams.pageSize ?? 10)) + 1;
    
    // ListQuery objesini hazırlarken, filtre ve sayfalama parametrelerini kullanıyoruz.
    const query = {
        pageIndex,
        pageSize: rows,
        filter: (dynamicFilter as Filter) ?? undefined,
        // Sorgu sayısını çekmek için 'queries' ilişkisini include et
        include: { queries: true } 
    }
    
    // Yeni generic metot çağrısı
    fetchDataDynamic(query as QueryParams);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, dynamicFilter]); 
  // first değişimi artık handlePage içinde yönetildiği için listeden çıkarıldı

  // DataTable body templates
  const activeBodyTemplate = (app: IntegrationApplication) => (
    <span className={app.isActive ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
      {app.isActive ? "Aktif" : "Pasif"}
    </span>
  );
  
  // Not: Generic Store sadece data döndürürse, queries?.length alanı için sunucuda
  // "include: { queries: true }" ile çekildiğinden emin olmalısınız.
  const queriesBodyTemplate = (app: IntegrationApplication) => (
    <span className="font-medium">
      {app.queries?.length || 0}
    </span>
  );

  const actionBodyTemplate = (app: IntegrationApplication) => (
    <div className="flex gap-2"><IconButton 
        onClick={() => handleManageQueries(app)} 
        icon={ArrowRight} 
        className="btn-sm text-green-600 hover:text-green-800"  
      />
      <IconButton onClick={() => handleEdit(app)} icon={Edit} className="btn-sm text-blue-600 hover:text-blue-800" />
      <IconButton onClick={() => handleDeleteClick(app)} icon={Trash2} className="btn-sm text-red-500 hover:text-red-700" />
    </div>
  );

  return (
    <div className="w-full">
      {confirmModal}

      <DataTable
        // applications'ı artık "data" olarak generic store'dan alıyoruz
        value={applications} 
        header={header}
        paginator
        lazy
        emptyMessage="Kayıt bulunamadı."
        first={first}
        rows={rows}
        totalRecords={appTotal}
        onPage={handlePage}
        loading={appLoading}
        rowsPerPageOptions={[5, 10, 20, 50]}
        filterDisplay="menu"
        scrollable
        scrollHeight={scrollHeight}
      >
        <Column field="id" header="ID" />
        <Column field="name" header="Uygulama Adı" />
        <Column field="externalType" header="Tip" />
        <Column header="Sorgu Sayısı" body={queriesBodyTemplate} align="center" style={{ width: "8rem" }}/>
        <Column field="isActive" header="Aktif" dataType="boolean" body={activeBodyTemplate} />
        <Column header="İşlemler" body={actionBodyTemplate} align="center" style={{ width: "8rem" }} />
      </DataTable>

      <IntegrationAppDialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        app={appToEdit}
        onSave={handleSaveApp}
      />
      {appForQueries && (
          <IntegrationQueryList
              isOpen={!!appForQueries}
              onClose={handleCloseQueryModal}
              application={appForQueries} // Seçili Uygulama Bilgisini Query Listesine gönder
          />
      )}
    </div>
  );
};